import produce from "immer";
import { isEmpty } from "psims/lib/empty";
import { recordActionAsEnum, recordActionFromEnum } from "psims/models/api/data-submission-record-action";
import { MsoAnnualActivityRefiner } from "psims/models/submission-types/mso/annual-activity/refiner/mso-annual-activity-refiner";
import { UpdateMsoAnnualActivityRefiner, UpdateMsoAnnualActivityRefinerDraft, UpdateMsoAnnualActivityRefinerField } from "psims/models/submission-types/mso/annual-activity/refiner/update-mso-annual-activity-refiner";
//import { UpdateMsoStockholdingRefinerDraft } from "psims/models/submission-types/mso/annual-activity/refiner/update-mso-stockholding-refiner";
import { UpdateMsoStockholdingImportDraft } from "psims/models/submission-types/mso/annual-activity/importer/update-mso-stockholding-import";

import { UseFocusedField } from "psims/react/util/use-focused-field";
import { useCallback, useEffect, useMemo, useState } from "react";
import { cleanSubmissionEntityList } from "../../../shared/update-data-utils";
import useTrackChanges from "../../../shared/use-track-changes";
import { FieldAlert, FieldValidation, FocusField } from "../../shared/types";
import { UseMsoAnnualActivityRefData } from "../../shared/use-mso-annual-activity-ref-data";
import { getInlineInfoMessage, getInlineValidationMessage } from "./refiner-annual-activities-inline-messages";
import { getInfoNotificationMessage, getValidationNotificationMessage } from "./refiner-annual-activities-notifications";
import { validateImporterAnnualActivity, ValidationResult } from "./refiner-annual-activities-validation";
import { ValidationAlertResult, validationAlertsForRefinerAnnualActivity } from "./refiner-annual-activities-validation-alerts";
import { computeIndicativeMSO } from "./shared"; // ← add

interface ProgressController {
  currentStep: {
    kind: 'data' | 'submit';
  };
  goToStep: (index: number) => any;
}

interface UseRefinerAnnualActivitiesProps {
  dataSubmissionId: number;
  focusFieldCtrl: UseFocusedField<FocusField>;
  imports: Array<UpdateMsoStockholdingImportDraft>;
  initialAnnualActivities: Array<MsoAnnualActivityRefiner>;
  progressCtrl: ProgressController;
  refData: UseMsoAnnualActivityRefData;
  msoTargetDays: Array<{ msoProductId: number; noOfDays: number; fuelTypeThreshHold: number }>;
}

type UpdateMsoAnnualActivityImporterDraft = Partial<UpdateMsoAnnualActivityRefiner> & {
  msoProductId: number;
  _id: symbol
};

function useRefinerAnnualActivities({
  dataSubmissionId,
  focusFieldCtrl,
  imports,
  initialAnnualActivities,
  progressCtrl,
  refData,
  msoTargetDays,
}: UseRefinerAnnualActivitiesProps) {
  const initialAnnualActivitiesAsUpdate = useMemo(() => {
    return refData.products
    .filter(p => p.productStatus === 'active') 
    .map(({id}) => {
      const annualActivity = initialAnnualActivities.find(aa => aa.msoProductId === id);
      return toUpdateMsoAnnualActivityRefinerDraft(dataSubmissionId, id, annualActivity);
    })
  }, [dataSubmissionId, initialAnnualActivities, refData.products]);

  const [data, setData] = useState(initialAnnualActivitiesAsUpdate);

  const changeCtrl = useTrackChanges({
    currentData: data,
    dataFields: ['enforceableArrangements', 'totalImportVolumeSelf'], // ← add totalImportVolumeSelf
    initialData: initialAnnualActivitiesAsUpdate,
  });

  const updateAnnualActivity = useCallback((annualActivity: UpdateMsoAnnualActivityRefinerDraft) => {
    setData(prev => produce(prev, draft => {
      const idx = data.findIndex(s => s._id === annualActivity._id);
      draft[idx] = {
        ...annualActivity,
        recordAction: recordActionAsEnum(annualActivity.id == null ? 'Create' : 'Update'),
      }
    }));
  }, [data]);

  const deleteAnnualActivityByProductId = useCallback((productId: number) => {
    setData(prev => {
      return prev
        .filter(d => d.msoProductId !== productId || d.id != null)
        .map(d => {
          if (d.msoProductId !== productId) {
            return d;
          }
          return {
            ...d,
            recordAction: recordActionAsEnum('Delete'),
          }
        })
    })
  }, []);

  const dataView = useMemo(() => {
    return data
      .filter(d => recordActionFromEnum(d.recordAction) !== 'Delete')
      .map(a => {
        const activeImports = imports.filter(i => recordActionFromEnum(i.recordAction) !== 'Delete');
        const totalImportsByOthers = calculateTotalImports(activeImports, a.msoProductId, false);
        const totalImportsOnBehalf = calculateTotalImports(activeImports, a.msoProductId, true);
        const totalAnnualVolume = calculateTotalAnnualVolume(a, totalImportsByOthers, totalImportsOnBehalf);
        const indicativeMSOBasedVolume = computeIndicativeMSO(totalAnnualVolume, a.msoProductId, msoTargetDays) ?? undefined;
        console.log('computeIndicativeMSO:', { totalAnnualVolume, msoProductId: a.msoProductId, msoTargetDays, result: indicativeMSOBasedVolume });
        const data = {
          ...a,
          totalAnnualVolume,
          totalImportVolumeOthers: totalImportsByOthers,
          totalImportVolumeOnBehalf: totalImportsOnBehalf,
          indicativeMSOBasedVolume,
        };
        return {
          data,
          totalAnnualVolume,
          indicativeMSOBasedVolume,
          validations: validateImporterAnnualActivity(data, refData.productIdMap),
          validationAlerts: validationAlertsForRefinerAnnualActivity(data, totalAnnualVolume, activeImports),
        }
      })
      .map(s => ({
        data: s.data,
        validations: s.validations.reduce((map, validationResult) => ({
          ...map,
          [validationResult.key]: {
            inline: getInlineValidationMessage({...validationResult}),
            notification: {
              parts: getValidationNotificationMessage(
                validationResult.key,
                () => {
                  const {code} = validationResult;
                  if (code === 'inactive_product') {
                    if (progressCtrl.currentStep.kind === 'submit') {
                      progressCtrl.goToStep(0);
                    }
                    focusFieldCtrl.setFocusedField({
                      field: 'delete',
                      kind: 'annualActivities',
                      productId: s.data.msoProductId
                    })
                  } else {
                    focusFieldCtrl.setFocusedField({_id: s.data._id, field: validationResult.key, kind: 'data'})
                  }
                },
                refData.productIdMap[s.data.msoProductId].name,
                validationResult
              )
            },
            validationResult
          }
        }), {} as {[key in UpdateMsoAnnualActivityRefinerField | 'delete']?: FieldValidation<ValidationResult>}),
        validationAlerts: s.validationAlerts.reduce((map, validationAlert) => ({
          ...map,
          [validationAlert.key]: {
            inline: getInlineInfoMessage(validationAlert),
            notification: {
              parts: getInfoNotificationMessage(
                validationAlert.key,
                () => focusFieldCtrl.setFocusedField({_id: s.data._id, field: validationAlert.key === 'totalAnnualVolume' ? 'totalImportVolumeSelf' : validationAlert.key, kind: 'data'}),
                () => focusFieldCtrl.setFocusedField({kind: 'comments'}),
                refData.productIdMap[s.data.msoProductId].name,
                validationAlert
              )
            },
            validationAlert,
          }
        }), {} as {[key in (UpdateMsoAnnualActivityRefinerField | 'totalAnnualVolume')]?: FieldAlert<ValidationAlertResult>}),
        totalAnnualVolume: s.totalAnnualVolume,
        indicativeMso: s.indicativeMSOBasedVolume ?? null, // ← expose for editor
      }))
  }, [data, focusFieldCtrl, imports, msoTargetDays, progressCtrl, refData]);

  const requestEntities = useMemo(() => {
    return cleanSubmissionEntityList(
      dataView
        .map(d => {
          const imprts = imports.filter(i => (
            i.msoProductId === d.data.msoProductId &&
            i.recordAction != null
          ));

          if (d.data.recordAction != null) {
            return d.data;
          }

          return imprts.length > 0 ? {
            ...d.data,
            recordAction: d.data.id == null ? recordActionAsEnum('Create') : recordActionAsEnum('Update'),
          } : d.data;
        })
    )
  }, [dataView, imports]);

  const requiresComments = useMemo(() => {
    return dataView
      .filter(d => d.validationAlerts.totalImportVolumeOnBehalf != null || d.validationAlerts.totalImportVolumeOthers != null)
      .length > 0
  }, [dataView]);

  const anyDeleted = useMemo(
    () => data.some(d => recordActionFromEnum(d.recordAction) === 'Delete'),
    [data]
  );

  const anyInvalid = useMemo(
    () => dataView.some(v => !isEmpty(v.validations)),
    [dataView]
  );

  const currentStepKind = progressCtrl.currentStep.kind;

  useEffect(() => {
    setData(initialAnnualActivitiesAsUpdate);
  }, [initialAnnualActivitiesAsUpdate]);

  useEffect(() => {
    if (currentStepKind !== 'data') {
      setData(initialAnnualActivitiesAsUpdate);
    }
  }, [initialAnnualActivitiesAsUpdate, currentStepKind]);

  return {
    anyInvalid,
    deleteAnnualActivityByProductId,
    hasChanges: changeCtrl.hasChanges || anyDeleted,
    requestEntities,
    requiresComments,
    updateAnnualActivities: dataView,
    updateAnnualActivity,
  }
}

function toUpdateMsoAnnualActivityRefinerDraft(
  dataSubmissionId: number,
  msoProductId: number,
  annualActivity?: MsoAnnualActivityRefiner
): UpdateMsoAnnualActivityRefinerDraft {
  let {recordResult, ...data} = (annualActivity || {});

  if (isEmpty(data)) {
    data = {
      dataSubmissionId,
      msoProductId,
      totalAnnualVolume: null,
      totalStorageCapacity: 0,
      totalImportVolumeSelf: undefined, 
      enforceableArrangements: false,
    }
  }

  return {
    ...data as UpdateMsoAnnualActivityRefinerDraft,
    enforceableArrangements: (data as UpdateMsoAnnualActivityRefinerDraft).enforceableArrangements || false,
    totalImportVolumeSelf: (data as UpdateMsoAnnualActivityRefinerDraft).totalImportVolumeSelf ?? undefined, // ← add
    recordAction: undefined,
    _id: Symbol(),
  };
}

// Refiner total = self + imported by others (on behalf excluded — that's volume they imported FOR others)
function calculateTotalAnnualVolume(
  data: UpdateMsoAnnualActivityRefinerDraft,
  totalImportsByOthers: number,
  totalImportsOnBehalf: number
): number {
  return (
    (data.totalImportVolumeSelf || 0) +
    (totalImportsByOthers || 0)
  );
}

function calculateTotalImports(
  data: Array<UpdateMsoStockholdingImportDraft>,
  msoProductId: number,
  isOnBehalf: boolean
): number {
  return data
    .filter(imprt => imprt.msoProductId === msoProductId && Boolean(imprt.isOnBehalf) === isOnBehalf)
    .reduce((sum, imprt) => sum + (imprt.amountImported || 0), 0);
}

export default useRefinerAnnualActivities;

export type UseRefinerAnnualActivities = ReturnType<typeof useRefinerAnnualActivities>;